/** A host name as `URL` leaves it - lowercased, an IDN already in punycode: dot-separated letter/digit/hyphen labels. */
const DNS_HOST = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)*$/
const IPV6_HOST = /^\[[0-9a-f:.]+\]$/

/**
 * True for an http(s) URL whose host is a real host name or IP address, so the origin it is reduced to can match
 * what a browser sends. Any other scheme (ftp, file, javascript…) is rejected, and so is a host `URL` happens to
 * accept but no browser ever sends as an `Origin`: a wildcard (`https://*.x.sk`, `%2A`), an underscore, an empty
 * label, a trailing dot. An address carrying a user name or password is rejected too - it is never an origin.
 */
export function isEmbeddableUrl(value: string): boolean {
  let url: URL
  try {
    url = new URL(value)
  } catch {
    return false
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return false
  if (url.username || url.password) return false
  return DNS_HOST.test(url.hostname) || IPV6_HOST.test(url.hostname)
}

/**
 * The origin (`scheme://host[:port]`) of a URL, or `''` when it does not parse. That is the form a browser sends as
 * the CORS `Origin` header: the host lowercased, a default port dropped, the path and query gone.
 */
export function originOf(url: string): string {
  try {
    return new URL(url.trim()).origin
  } catch {
    return ''
  }
}

/**
 * A premise's embedding addresses as they are stored: each reduced to its origin, so a whole site is allowed and
 * never a single path, without duplicates, in the order first given. An address that does not parse is dropped.
 */
export function uniqueOrigins(urls: readonly string[]): string[] {
  return [...new Set(urls.map(originOf).filter(Boolean))]
}

/**
 * The indexes of the addresses whose origin an earlier address already has - the rows the editor flags, since
 * saving would silently collapse them into the first. An address that does not parse is never a duplicate.
 */
export function duplicateOriginIndexes(urls: readonly string[]): number[] {
  const seen = new Set<string>()
  const duplicates: number[] = []
  urls.forEach((url, index) => {
    const origin = originOf(url)
    if (!origin) return
    if (seen.has(origin)) duplicates.push(index)
    seen.add(origin)
  })
  return duplicates
}
